// src/services/socket.ts
// Aligned with carepro_backend/shared/socket-client/README.md

import { io, Socket } from "socket.io-client";
import { BACKEND_URL } from "../Apis/auth";
import { trackSocketDisconnected } from "../analytics";

/** Connection state matching backend README */
export type ConnectionState =
  "disconnected" | "connecting" | "connected" | "reconnecting";

/** Options for connectSocket (aligned with CareProSocketClient constructor) */
export interface SocketConnectOptions {
  serverUrl?: string;
  token?: string;
  autoReconnect?: boolean;
  maxReconnectAttempts?: number;
  reconnectDelay?: number;
  transports?: ("websocket" | "polling")[];
  debug?: boolean;
}

// Singleton socket instance
let socket: Socket | null = null;
let connectionState: ConnectionState = "disconnected";

// Reliability-signal state (W2-R / A.15): session start + reconnect churn.
let connectedAtMs: number | null = null;
let reconnectAttemptCount = 0;

// Track wrapped handlers so offSocket can remove the correct function
const handlerWrapperMap = new Map<
  string,
  Map<(...args: any[]) => void, (...args: any[]) => void>
>();

const storeHandlerWrapper = (
  event: string,
  original: (...args: any[]) => void,
  wrapped: (...args: any[]) => void
) => {
  const eventMap =
    handlerWrapperMap.get(event) ??
    new Map<(...args: any[]) => void, (...args: any[]) => void>();
  eventMap.set(original, wrapped);
  handlerWrapperMap.set(event, eventMap);
};

const takeHandlerWrapper = (
  event: string,
  original: (...args: any[]) => void
): ((...args: any[]) => void) | undefined => {
  const eventMap = handlerWrapperMap.get(event);
  if (!eventMap) return undefined;
  const wrapped = eventMap.get(original);
  if (wrapped) {
    eventMap.delete(original);
    if (eventMap.size === 0) {
      handlerWrapperMap.delete(event);
    }
  }
  return wrapped;
};

const clearHandlerWrappersForEvent = (event: string) => {
  handlerWrapperMap.delete(event);
};

function attachStoredHandlers(sock: Socket): void {
  handlerWrapperMap.forEach((eventMap, event) => {
    eventMap.forEach((wrapped) => {
      sock.on(event, wrapped);
    });
  });
}

function getSocketAuthToken(sock: Socket): string | undefined {
  const auth = sock.auth as { token?: string } | undefined;
  return typeof auth?.token === "string" ? auth.token : undefined;
}

/**
 * Connect socket (optionally with auth token).
 * Uses auth: { token } per Socket.IO handshake and backend README.
 */
export const connectSocket = (
  token?: string,
  options: Partial<SocketConnectOptions> = {}
): Socket => {
  const {
    serverUrl = BACKEND_URL,
    autoReconnect = true,
    maxReconnectAttempts = 5,
    reconnectDelay: reconnectDelayMs = 1000,
    transports = ["polling", "websocket"],
    debug = false,
  } = options;

  if (!socket) {
    connectionState = "connecting";
    // withCredentials: false so CORS works when backend uses origin "*" (credentials + * is invalid in browsers)
    socket = io(serverUrl, {
      withCredentials: false,
      // Backend expects: socket.handshake.auth.token (a plain string)
      auth: { token },
      transports,
      reconnection: autoReconnect,
      reconnectionAttempts: maxReconnectAttempts,
      reconnectionDelay: reconnectDelayMs,
      reconnectionDelayMax: 5000,
    });

    socket.on("connect", () => {
      try {
        connectionState = "connected";
        connectedAtMs = Date.now();
        if (debug) {
          console.log("✅ Socket connected. Socket ID:", socket?.id);
        }
      } catch (err) {
        console.error("[Socket] Error in 'connect' handler:", err);
      }
    });

    socket.on("disconnect", (reason: string) => {
      try {
        connectionState = "disconnected";
        if (debug) {
          console.log("⚠️ Socket disconnected:", reason);
        }
        // Reliability signal (A.15): unexpected drops only — the deliberate
        // logout disconnect is not a reliability event. Rate-limited 1/60s
        // inside the helper; the helper is fail-open by contract.
        if (reason !== "io client disconnect") {
          trackSocketDisconnected({
            reason,
            // "Attempts it took to establish the connection that just
            // dropped" — socket.io fires reconnect_attempt only BETWEEN a
            // disconnect and the next connect, so resetting on connect made
            // this constant 0 (review MAJOR). Reset AFTER the emit instead.
            reconnectAttempts: reconnectAttemptCount,
            sessionAgeMs:
              connectedAtMs != null ? Date.now() - connectedAtMs : undefined,
          });
        }
        reconnectAttemptCount = 0;
      } catch (err) {
        console.error("[Socket] Error in 'disconnect' handler:", err);
      }
    });

    socket.on("connect_error", (err: Error) => {
      try {
        connectionState = "disconnected";
        console.error("❌ Socket connection error:", err.message);
      } catch (handlerErr) {
        console.error("[Socket] Error in 'connect_error' handler:", handlerErr);
      }
    });

    socket.io.on("reconnect_attempt", () => {
      try {
        connectionState = "reconnecting";
        reconnectAttemptCount += 1;
        if (debug) console.log("🔄 Reconnecting...");
      } catch (err) {
        console.error("[Socket] Error in 'reconnect_attempt' handler:", err);
      }
    });

    socket.io.on("reconnect", () => {
      try {
        connectionState = "connected";
        if (debug) console.log("✅ Socket reconnected");
      } catch (err) {
        console.error("[Socket] Error in 'reconnect' handler:", err);
      }
    });

    // Bind listeners that subscribed before the singleton existed (My Tasks,
    // notifications, etc.). Without this, onSocket() was a silent no-op and
    // the UI only updated after a full page refresh.
    attachStoredHandlers(socket);
  } else if (token && socket) {
    const currentToken = getSocketAuthToken(socket);
    if (currentToken !== token) {
      updateSocketToken(token);
    }
  }
  return socket;
};

/** Get socket instance (throws if not created yet) */
export const getSocket = (): Socket => {
  if (!socket) throw new Error("Socket not connected yet!");
  return socket;
};

/** Get socket instance or null if not created */
export const getSocketOrNull = (): Socket | null => socket;

/** Check if socket exists and is connected */
export const isConnected = (): boolean => !!socket?.connected;

/** Get current connection state (per README) */
export const getConnectionState = (): ConnectionState => {
  if (!socket) return "disconnected";
  if (socket.connected) return "connected";
  if (connectionState === "reconnecting") return "reconnecting";
  if (connectionState === "connecting") return "connecting";
  return "disconnected";
};

/** Disconnect socket */
export const disconnectSocket = (): void => {
  if (socket) {
    socket.disconnect();
    socket = null;
    connectionState = "disconnected";
    if (process.env.NODE_ENV === "development") {
      console.log("Socket disconnected manually");
    }
  }
};

/** Emit event (generic) */
export const emitSocket = (event: string, data?: unknown): void => {
  socket?.emit(event, data);
};

/**
 * Subscribe to event. Returns unsubscribe function (per README).
 */
export const onSocket = (
  event: string,
  handler: (...args: any[]) => void
): (() => void) => {
  const wrappedHandler = (...args: any[]) => {
    try {
      handler(...args);
    } catch (err) {
      console.error(`[Socket] Error in handler for event "${event}":`, err);
    }
  };

  storeHandlerWrapper(event, handler, wrappedHandler);
  socket?.on(event, wrappedHandler);

  return () => offSocket(event, handler);
};

/** Remove listener(s) for event */
export const offSocket = (
  event: string,
  handler?: (...args: any[]) => void
): void => {
  if (handler) {
    const wrapped = takeHandlerWrapper(event, handler);
    if (wrapped) {
      socket?.off(event, wrapped);
    } else {
      socket?.off(event, handler);
    }
    return;
  }
  clearHandlerWrappersForEvent(event);
  socket?.off(event);
};

/** Update JWT token and reconnect (per README) */
export const updateSocketToken = (token: string): void => {
  if (!socket) return;
  socket.auth = { token };
  socket.disconnect();
  socket.connect();
};

// --- Client-to-server events (per README "Client to Server Events") ---

/** Join patient room */
export const joinPatient = (patientId: string): void => {
  emitSocket("patient:join", { patientId });
};

/** Leave patient room */
export const leavePatient = (patientId: string): void => {
  emitSocket("patient:leave", { patientId });
};

/** Join contact room (mirrors patient:join for contact-only leads) */
export const joinContact = (contactId: string): void => {
  emitSocket("contact:join", { contactId });
};

/** Leave contact room */
export const leaveContact = (contactId: string): void => {
  emitSocket("contact:leave", { contactId });
};

/** Start call (acquire call lock) */
export const startCall = (payload: {
  phoneNumber: string;
  patientId?: string;
}): void => {
  emitSocket("call:start", payload);
};

/** End call (release call lock) */
export const endCall = (payload: {
  phoneNumber?: string;
  duration?: number;
  outcome?: string;
}): void => {
  emitSocket("call:end", payload);
};

/** Update opportunity status */
export const updateOpportunityStatus = (payload: {
  opportunityId: string;
  status: string;
  disposition?: string;
}): void => {
  emitSocket("opportunity:update:status", payload);
};

/** Send typing indicator */
export const sendTypingIndicator = (patientId: string): void => {
  emitSocket("user:typing", { patientId });
};
